<template>
  <el-dialog :visible.sync="isModalVisible" :show-close="false" :close-on-click-modal="false">
    <span slot="title" class="font-weight-bold h4">
      <div class="d-flex justify-content-center">
        <h3 v-if="isModalVisible && isCancelingOrder">Cancel Order</h3>
        <h3 v-else-if="isModalVisible && isConfirmingOrder">Confirm Order</h3>
        <h3 v-else-if="isModalVisible && isCancellingLineItem">Remove Item</h3>
        <h3 v-else-if="isModalVisible">Edit Order</h3>
      </div>
    </span>
    <el-row type="flex" justify="center">
      <el-col :span="24">
        <el-row type="flex" justify="center" v-if="isCancelingOrder || isCancellingLineItem">
          <el-col :span="2">
            <i v-show="isModalVisible" class="fa fa-exclamation-triangle text-t_red" style="font-size: 50px"></i>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center" :class="{ 'pt-5': isCancelingOrder }">
          <el-col :span="20">
            <h6
              class="text-center font-weight-bold py-1"
              v-if="!isCancelingOrder && !isCancelingOrder && !isCancellingLineItem"
            >
              This will send a notification to your customer to inform them of the change.
            </h6>
            <h6 class="text-center font-weight-bold py-1" v-if="isCancelingOrder || isCancellingLineItem">
              This will send a notification to your customer.
            </h6>
            <h6 class="text-center font-weight-bold py-1" v-if="isConfirmingOrder">
              Confirming the order will send an email notification to the customer.
            </h6>
            <h6 class="text-center font-weight-bold py-1" v-if="!isConfirmingOrder">
              Would you like to continue? {{ isCancellingLineItem ? "This action cannot be undone." : "" }}
            </h6>
            <h6 class="text-center font-weight-bold py-1">
              You can also add a (optional) message using the form below
            </h6>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" class="pt-2">
      <el-col :span="20" class="text-center">
        <el-input type="textarea" :rows="6" placeholder="Enter message.." v-model="customerFeedback"> </el-input>
      </el-col>
    </el-row>
    <span slot="footer" class="dialog-footer-center">
      <el-row :gutter="20">
        <el-button size="small" class="btn btn-neutral" @click="cancelSave">
          {{ isComingFromOrderCapture ? "Close" : "Back" }}</el-button
        >
        <el-button
          size="small"
          class="btn"
          :class="isCancelingOrder || isCancellingLineItem ? 'btn-destructive' : 'btn-confirm'"
          @click="confirmSave"
          :loading="btnLoading"
          >Send Notification</el-button
        >
      </el-row>
    </span>
  </el-dialog>
</template>

<script>
import breakpoint from "@/mixins/index";
import { updateOrder, updateOrderStatus, splitOrderItem, cancelLineItems } from "@/api/orders";
import { formatStringToNumber } from "@/util/util";
import { middayISODateFormat } from "@/util/filters";

export default {
  components: {},
  mixins: [breakpoint],
  props: {
    orderId: {
      type: String,
      required: true,
      default: "",
    },
    lineItemIndices: {
      type: Array,
      required: false,
      default: () => [],
    },
    orderToUpdate: {
      type: Object,
      required: true,
      default: () => {},
    },
    isCancelingOrder: {
      type: Boolean,
      required: false,
      default: false,
    },
    viewEditDeleteConfirmationDialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    viewEditOrderDialogVisible: {
      type: Boolean,
      required: true,
      default: false,
    },
    viewEditOrderDialog: {
      type: Boolean,
      required: false,
    },
    isBunkering: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConfirmingOrder: {
      type: Boolean,
      required: false,
      default: false,
    },
    isComingFromOrderCapture: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCancellingLineItem: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      customerFeedback: "",
      orderData: null,
      btnLoading: false,
      isModalVisible: true,
    };
  },
  computed: {},
  mounted() {
    this.orderData = JSON.parse(JSON.stringify(this.orderToUpdate));
  },
  methods: {
    displayPreviousModal() {
      this.isModalVisible = false;
      this.$emit("update:isCancelingOrder", false);
      setTimeout(() => {
        this.$emit("update:viewEditOrderDialogVisible", true);
        this.$emit("update:viewEditDeleteConfirmationDialog", false);
      }, 150);
    },
    displayAndRefreshPreviousModal() {
      this.isModalVisible = false;
      this.$emit("update:isCancelingOrder", false);
      this.$emit("refreshModal");
      setTimeout(() => {
        this.$emit("update:viewEditOrderDialogVisible", true);
        this.$emit("update:viewEditDeleteConfirmationDialog", false);
      }, 150);
    },
    cancelSave() {
      if (this.isComingFromOrderCapture) {
        this.$emit("closeEditOrderDialog");
      } else {
        this.displayPreviousModal();
      }
    },
    formatDate(date) {
      if (date) {
        return middayISODateFormat(date);
      }
    },
    async confirmSave() {
      this.btnLoading = true;
      try {
        if (this.isCancelingOrder) {
          updateOrderStatus(this.orderId, { orderCancelled: true, feedback: this.customerFeedback }).then(
            this.handleResponse
          );
        } else if (this.isConfirmingOrder) {
          updateOrderStatus(this.orderId, { status: "CONFIRMED", feedback: this.customerFeedback }).then(
            this.handleResponse
          );
        } else if (this.isCancellingLineItem) {
          const lineItemDto = this.orderData.offers.map((o, i) => {
            return { ...o, lineItemCancelled: this.lineItemIndices.includes(i) };
          });
          cancelLineItems(this.orderId, lineItemDto).then(this.handleResponse);
        } else {
          await this.createOrderDTO();
          if (this.isBunkering) {
            this.formatDTOforBunkering();
          }
          for (const splitOffer of this.orderData.splitOffers) {
            await splitOrderItem(splitOffer);
          }
          // include the reference, location and delivery date only if they have changed
          const tempOrder = { offers: this.orderData.offers };
          if (this.orderData.client_reference) {
            tempOrder.client_reference = this.orderData.client_reference;
          }
          if (this.orderData.location) {
            tempOrder.location = this.orderData.location;
            tempOrder.location_id = this.orderData.location._id;
          }
          if (this.orderData.deliveryDate) {
            tempOrder.deliveryDate = this.orderData.deliveryDate;
          }
          updateOrder(this.orderId, tempOrder, this.customerFeedback).then(this.handleResponse);
        }
      } catch (ex) {
        console.dir(ex);
        this.displayPreviousModal();
      }
    },
    formatDTOforBunkering() {
      this.orderData.offers.forEach(async (element) => {
        delete element.collectionTime;
        delete element.liftingPeriod;
      });
    },
    async createOrderDTO() {
      await this.orderData.offers.forEach(async (offer) => {
        if (offer.liftingPeriod) {
          offer.liftingPeriod = [this.formatDate(offer.liftingPeriod[0]), this.formatDate(offer.liftingPeriod[1])];
        }
        if (offer.price) {
          offer.price = formatStringToNumber(offer.price);
        }
        if (offer.quantityOrdered) {
          offer.quantityOrdered = formatStringToNumber(offer.quantityOrdered);
        }
        if (offer.collectionTime) {
          offer.collectionTime = -1;
        }
      });
      if (this.orderData.deliveryDate) {
        this.orderData.deliveryDate = this.formatDate(this.orderData.deliveryDate);
      }
      this.orderData.splitOffers.forEach((offer) => {
        offer.price = formatStringToNumber(offer.price);
        offer.quantity = formatStringToNumber(offer.quantity);
      });
    },
    handleResponse(res) {
      if (res.status === 200) {
        this.btnLoading = false;
        this.displayAndRefreshPreviousModal();
      } else {
        this.displayPreviousModal();
      }
    },
  },
};
</script>
<style></style>
